import { API_BASE_URL } from "../config";
import { raiseApiError } from "../lib/api";
import { getAuthHeaders } from "./auth";

export interface IdentityProviderButtonMetadata {
  accentColor?: string;
  text?: string;
  icon?: string;
}

export interface IdentityProviderMetadata {
  button?: IdentityProviderButtonMetadata | null;
  [key: string]: unknown;
}

export interface IdentityProviderSummary {
  slug: string;
  displayName: string;
  providerType: string;
  default: boolean;
  metadata?: IdentityProviderMetadata | null;
}

export interface ListIdentityProvidersOptions {
  tenantId?: string;
  tenantSlug?: string;
}

export interface IdentityProviderAuthorizeInput {
  providerSlug: string;
  tenantId?: string;
  tenantSlug?: string;
  redirectUri: string;
  loginHint?: string;
}

export interface IdentityProviderAuthorizeResponse {
  authorizationUrl: string;
  state: string;
  expiresAt: number;
  method?: "GET" | "POST";
}

export async function listIdentityProviders(
  options?: ListIdentityProvidersOptions,
  apiBaseUrl: string = API_BASE_URL,
  signal?: AbortSignal
): Promise<IdentityProviderSummary[]> {
  const params = new URLSearchParams();

  if (options?.tenantId) {
    params.set("tenantId", options.tenantId);
  }

  if (options?.tenantSlug) {
    params.set("tenantSlug", options.tenantSlug);
  }

  const query = params.toString();
  const url = `${apiBaseUrl}/api/auth/providers${query ? `?${query}` : ""}`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
    credentials: "include",
    signal
  });

  if (!response.ok) {
    await raiseApiError(response, `Identity providers request failed (${response.status})`);
  }

  const payload = (await response.json().catch(() => null)) as
    | { providers?: IdentityProviderSummary[] }
    | null;

  if (!payload || !Array.isArray(payload.providers)) {
    return [];
  }

  return payload.providers as IdentityProviderSummary[];
}

export async function authorizeIdentityProvider(
  input: IdentityProviderAuthorizeInput,
  apiBaseUrl: string = API_BASE_URL
): Promise<IdentityProviderAuthorizeResponse> {
  const url = `${apiBaseUrl}/api/auth/providers/${encodeURIComponent(input.providerSlug)}/authorize`;

  console.log('[SSO] Authorize Identity Provider - Request Details:', {
    apiBaseUrl,
    fullUrl: url,
    providerSlug: input.providerSlug,
    redirectUri: input.redirectUri,
    origin: window.location.origin,
    hostname: window.location.hostname
  });

  const requestBody = {
    tenantId: input.tenantId,
    tenantSlug: input.tenantSlug,
    redirectUri: input.redirectUri,
    loginHint: input.loginHint ?? undefined
  };

  console.log('[SSO] Request body:', requestBody);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: JSON.stringify(requestBody)
  });

  console.log('[SSO] Response status:', response.status);
  console.log('[SSO] Response headers:', Object.fromEntries(response.headers.entries()));

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[SSO] Authorization failed:', {
      status: response.status,
      statusText: response.statusText,
      errorText
    });
    await raiseApiError(response, `Identity provider authorize failed (${response.status})`);
  }

  const result = await response.json() as IdentityProviderAuthorizeResponse;
  console.log('[SSO] Authorization success:', result);
  return result;
}
